import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
//CSS
import styles from "./ContextMenu.module.css";
import { ContextMenuItem } from "../../../Types/ContextMenuItem";
import Tooltip from "../Tooltip/Tooltip";
import ConfirmAction from "../ConfirmAction/ConfirmAction";
//Components
//Data

type ContextMenuProps = {
  children: React.ReactNode;
  items: ContextMenuItem[];
  contextId: string;
  position?: "left" | "right";
  disabled?: boolean
  disabledTooltip?: string;
};

const ContextMenu = ({ children, items, contextId, position, disabled, disabledTooltip }: ContextMenuProps) => {
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingItem, setPendingItem] = useState<ContextMenuItem | null>(null);
  const [menuBox, setMenuBox] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!showMenu || !menuRef.current) {
      setMenuBox(null);
      return;
    }
    const trigger = menuRef.current.getBoundingClientRect();
    const menu = menuRef.current.querySelector(`.${styles.contextMenuItems}`) as HTMLElement | null;
    const width = menu?.offsetWidth || 168;
    const height = menu?.offsetHeight || 120;
    let left = position === "left" ? trigger.left + 5 - width : trigger.right - 5;
    let top = trigger.top + 15;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    if (top + height > window.innerHeight - 8) top = Math.max(8, trigger.top - height - 4);
    setMenuBox({ top, left });
  }, [showMenu, position, items]);

  const onClickHandler = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMenu(true);
  };

const onItemClickHandler = async (event: React.MouseEvent, item: ContextMenuItem) => {
  event.preventDefault();
  event.stopPropagation();
  if (loading || item.disabled || disabled) {
    return;
  }

  if (item.requiresConfirmation) {
    setPendingItem(item);
    setShowConfirm(true);
    setShowMenu(false);
    return;
  }

  setShowMenu(false);
  try {
    setLoading(true);
    await item.onClickHandler(contextId);
  } catch (error) {
    console.error("Error executing item click handler:", error);
  } finally {
    setLoading(false);
  }
};

const handleConfirmAction = async () => {
  if (!pendingItem) return;
  
  try {
    setLoading(true);
    await pendingItem.onClickHandler(contextId);
  } catch (error) {
    console.error("Error executing item click handler:", error);
  } finally {
    setLoading(false);
    setShowConfirm(false);
    setPendingItem(null);
  }
};


  useEffect(() => {

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <div className={`${styles.container} tree`} data-open={showMenu ? "true" : undefined} onClick={(onClickHandler)} ref={menuRef}>
        <div
          className={`${styles.contextMenuItems} ${position === "left"? styles.left : styles.right} ${showMenu ? styles.show : ""}`}
          style={menuBox ? { position: "fixed", top: menuBox.top, left: menuBox.left, right: "auto" } : undefined}
        >
          {items.map((item, index) => (
              <Tooltip text={item.disabledTooltip ?? disabledTooltip ?? ""} align={"top"} sideAlign={"left"} disabled={!(item.disabled || disabled) || disabledTooltip === undefined}>
                <div className={`${styles.item} ${(loading || item.disabled || disabled) && styles.disabled}`} key={index} style={item.style} onClick={(event) => onItemClickHandler(event, item)}>
                  <p key={index}>{item.title}</p>
                  <img src={item.icon}/>
                </div>
              </Tooltip>
          ))}
        </div>
        <div className={styles.trigger}>
          {children}
        </div>
      </div>

      {pendingItem && (
        <ConfirmAction
          isVisible={showConfirm}
          onClose={() => {
            setShowConfirm(false);
            setPendingItem(null);
          }}
          clickHandler={handleConfirmAction}
          title={pendingItem.confirmationConfig?.title || `Confirm ${pendingItem.title}`}
          message={pendingItem.confirmationConfig?.message || `Are you sure you want to ${pendingItem.title.toLowerCase()}?`}
          severity={pendingItem.confirmationConfig?.severity || "danger"}
          icon={pendingItem.confirmationConfig?.icon || pendingItem.icon}
          loading={loading}
        />
      )}
    </>
  );
};

export default ContextMenu;
